import { createBrowserClient } from '@supabase/ssr';
import type { Database } from './database.types';
import { attachWriteSanitizer } from '@/lib/userText';

// Single shared instance — multiple clients cause token-refresh race conditions
// where one client invalidates the session while others hold a stale token.
//
// Do NOT use this client for email/password login. Browser signInWithPassword
// writes cookies too late for proxy.ts (bounce-to-/login with /token 200).
// Use Server Action loginWithPassword — .cursor/rules/auth-session-cookies.mdc.
let _client: ReturnType<typeof createBrowserClient<Database>> | null = null;

/**
 * Resolve public Supabase credentials.
 *
 * Vercel Preview historically only had Production env vars. Root `Providers`
 * (Auth/Students/Checkout/Capstone) call this during SSR of every page —
 * including static prerender of `/_not-found` — so missing Preview env made
 * `next build` exit 1 with:
 *   @supabase/ssr: Your project's URL and API key are required…
 *
 * During SSR/build without credentials we use inert placeholders so prerender
 * can finish. The browser bundle starts a fresh module scope and will throw
 * clearly if Preview/Production still lack the real NEXT_PUBLIC_* values.
 */
function resolvePublicCredentials(): { url: string; key: string } {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (url && key) return { url, key };

  if (typeof window === 'undefined') {
    return {
      url: 'http://127.0.0.1:54321',
      key: 'public-anon-key',
    };
  }

  throw new Error(
    'Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY. ' +
      'On Vercel, set both for Production AND Preview (Project → Settings → Environment Variables).'
  );
}

export function createClient() {
  if (!_client) {
    const { url, key } = resolvePublicCredentials();
    _client = attachWriteSanitizer(createBrowserClient<Database>(url, key));
  }
  return _client;
}
